import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Modal } from '../../components/Modal';
import { PaginationControls } from '../../components/PaginationControls';
import { FilterBar } from '../../components/FilterBar';
import { notificationService, Notification, NotificationStats } from '../../services/notificationService';

export const AdminNotifications: React.FC = () => {
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [formData, setFormData] = useState({
    type_notification: 'general',
    recipient_type: 'tous_actifs',
    subject: '',
    message: '',
    html_content: '',
    recipient_emails: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sending, setSending] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    status: '',
  });

  useEffect(() => {
    fetchStats();
    fetchNotifications(1);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, allNotifications, currentPage]);

  const fetchStats = async () => {
    try {
      const data = await notificationService.getStats();
      setStats(data);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const fetchNotifications = async (page: number = 1) => {
    setLoading(true);
    try {
      const data = await notificationService.getAll(1);
      const allNotificationsData = data.results || [];
      setAllNotifications(allNotificationsData);
      setTotalPages(Math.ceil((allNotificationsData.length || 0) / 10));
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allNotifications];

    // Filtre par type
    if (filters.type) {
      filtered = filtered.filter((notif) => notif.type_notification === filters.type);
    }

    // Filtre par statut
    if (filters.status) {
      const isSent = filters.status === 'sent';
      filtered = filtered.filter((notif) => notif.is_sent === isSent);
    }

    // Trier par date de création décroissante (plus récent en premier)
    filtered.sort((a, b) => {
      const dateA = new Date((a as any).created_at || 0).getTime();
      const dateB = new Date((b as any).created_at || 0).getTime();
      return dateB - dateA;
    });

    // Pagination
    const pageSize = 10;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedNotifications = filtered.slice(startIndex, endIndex);

    setNotifications(paginatedNotifications);
    setTotalPages(Math.ceil(filtered.length / pageSize));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: any = {
        type_notification: formData.type_notification,
        recipient_type: formData.recipient_type,
        subject: formData.subject,
        message: formData.message,
      };
      
      if (formData.html_content) {
        payload.html_content = formData.html_content;
      }
      
      if (formData.recipient_type === 'personnalise' && formData.recipient_emails) {
        payload.recipient_emails = formData.recipient_emails
          .split(',')
          .map((email) => email.trim())
          .filter((email) => email.length > 0);
      }
      
      await notificationService.create(payload);
      setIsCreateModalOpen(false);
      setFormData({
        type_notification: 'general',
        recipient_type: 'tous_actifs',
        subject: '',
        message: '',
        html_content: '',
        recipient_emails: '',
      });
      await fetchNotifications(currentPage);
      fetchStats();
      alert('✅ Notification créée avec succès !\n\n⚠️ Important : Pour envoyer l\'email, allez dans l\'historique et cliquez sur "Envoyer" à côté de la notification.');
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue lors de la création');
    }
  };

  const handlePreview = async (notification: Notification) => {
    try {
      const data = await notificationService.preview(notification.id);
      setPreviewHtml(data.html_content);
      setSelectedNotification(notification);
      setIsPreviewModalOpen(true);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la prévisualisation');
    }
  };

  const handleSendClick = (notification: Notification) => {
    setSelectedNotification(notification);
    setIsSendModalOpen(true);
  };

  const handleSend = async () => {
    if (!selectedNotification) return;
    
    setSending(true);
    try {
      const recipientEmails = selectedNotification.recipient_type === 'personnalise' 
        ? formData.recipient_emails.split(',').map(e => e.trim()).filter(e => e)
        : undefined;
      
      const result = await notificationService.send(selectedNotification.id, recipientEmails);
      alert(`✅ Notification envoyée avec succès à ${result.sent_count} destinataire(s)`);
      setIsSendModalOpen(false);
      await fetchNotifications(currentPage);
      fetchStats();
    } catch (error: any) {
      console.error('Erreur:', error);
      alert(error.response?.data?.error || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const notificationTypes = [
    { value: 'general', label: 'Annonce Générale', icon: '📢' },
    { value: 'nouvelle_fonctionnalite', label: 'Nouvelle Fonctionnalité', icon: '✨' },
    { value: 'maintenance', label: 'Maintenance', icon: '🔧' },
    { value: 'evenement_special', label: 'Événement Spécial', icon: '🎊' },
    { value: 'rappel', label: 'Rappel', icon: '⏰' },
  ];

  const recipientTypes = [
    { value: 'tous_actifs', label: 'Tous les utilisateurs actifs', icon: '👥' },
    { value: 'membres', label: 'Membres uniquement', icon: '👤' },
    { value: 'admins', label: 'Administrateurs uniquement', icon: '👑' },
    { value: 'personnalise', label: 'Liste personnalisée', icon: '📝' },
  ];

  if (loading && !stats) {
    return (
      <AdminLayout activeSection="notifications">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeSection="notifications">
      {/* En-tête */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold text-green-700">Notifications & Emails</h2>
          <p className="text-gray-600 mt-1">Gérez et envoyez des notifications à tous les utilisateurs</p>
        </div>
        <button 
          onClick={() => setIsCreateModalOpen(true)} 
          className="btn-primary text-lg px-6 py-3"
        >
          + Nouvelle Notification
        </button>
      </div>

      {/* Statistiques */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Utilisateurs Actifs</p>
                <p className="text-3xl font-bold text-green-700">{stats.active_users}</p>
                <p className="text-xs text-gray-500 mt-1">sur {stats.total_users} total</p>
              </div>
              <div className="text-4xl">👥</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Membres</p>
                <p className="text-3xl font-bold text-blue-700">{stats.members}</p>
              </div>
              <div className="text-4xl">👤</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Notifications Envoyées</p>
                <p className="text-3xl font-bold text-purple-700">{stats.notifications_sent}</p>
                <p className="text-xs text-gray-500 mt-1">{stats.notifications_pending} en attente</p>
              </div>
              <div className="text-4xl">📧</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-amber-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Total Notifications</p>
                <p className="text-3xl font-bold text-amber-700">{stats.total_notifications}</p>
              </div>
              <div className="text-4xl">📬</div>
            </div>
          </div>
        </div>
      )}

      {/* Filtres */}
      <FilterBar
        searchPlaceholder=""
        searchValue=""
        onSearchChange={() => {}}
        filters={[
          {
            key: 'type',
            label: 'Tous les types',
            value: filters.type,
            onChange: (value) => {
              setFilters({ ...filters, type: value });
              setCurrentPage(1);
            },
            options: [
              { value: 'general', label: '📢 Annonce Générale' },
              { value: 'nouvelle_fonctionnalite', label: '✨ Nouvelle Fonctionnalité' },
              { value: 'maintenance', label: '🔧 Maintenance' },
              { value: 'evenement_special', label: '🎊 Événement Spécial' },
              { value: 'rappel', label: '⏰ Rappel' },
            ],
          },
          {
            key: 'status',
            label: 'Tous les statuts',
            value: filters.status,
            onChange: (value) => {
              setFilters({ ...filters, status: value });
              setCurrentPage(1);
            },
            options: [
              { value: 'sent', label: '✅ Envoyées' },
              { value: 'pending', label: '⏳ En attente' },
            ],
          },
        ]}
        onClear={() => {
          setFilters({ type: '', status: '' });
          setCurrentPage(1);
        }}
      />

      {/* Historique des notifications */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-800">Historique des Notifications</h3>
          {notifications.some(n => !n.is_sent) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2">
              <p className="text-yellow-800 text-sm">
                <span className="font-semibold">💡 Astuce :</span> Les notifications avec le statut "⏳ En attente" doivent être envoyées manuellement.
              </p>
            </div>
          )}
        </div>
        
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-700"></div>
          </div>
        ) : notifications.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Sujet</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Type</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Destinataires</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Statut</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {notifications.map((notification) => (
                    <tr key={notification.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <div className="font-medium text-gray-900">{notification.subject}</div>
                        <div className="text-sm text-gray-500 mt-1 line-clamp-1">
                          {notification.message.substring(0, 60)}...
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-semibold">
                          {notification.type_notification_display}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-sm text-gray-700">
                          {notification.recipient_type_display}
                        </div>
                        {notification.is_sent && (
                          <div className="text-xs text-gray-500 mt-1">
                            {notification.sent_count} / {notification.recipient_count} envoyés
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        {notification.is_sent ? (
                          <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">
                            ✅ Envoyé
                          </span>
                        ) : (
                          <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-xs font-semibold">
                            ⏳ En attente
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600">
                        {new Date(notification.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handlePreview(notification)}
                            className="text-blue-600 hover:text-blue-800 font-semibold text-sm"
                          >
                            👁️ Voir
                          </button>
                          {!notification.is_sent && (
                            <button
                              onClick={() => handleSendClick(notification)}
                              className="text-green-600 hover:text-green-800 font-semibold text-sm"
                            >
                              📤 Envoyer
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              loading={loading}
              onPageChange={(page) => {
                setCurrentPage(page);
              }}
            />
          </>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📧</div>
            <p className="text-gray-600 text-lg">Aucune notification pour le moment</p>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="mt-4 btn-primary"
            >
              Créer la première notification
            </button>
          </div>
        )}
      </div>

      {/* Modal Création */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Nouvelle Notification"
        size="lg"
        footer={
          <div className="flex gap-4">
            <button onClick={() => setIsCreateModalOpen(false)} className="btn-secondary">
              Annuler
            </button>
            <button onClick={handleCreate} className="btn-primary">
              Créer
            </button>
          </div>
        }
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de notification *
            </label>
            <select
              className="input-field"
              value={formData.type_notification}
              onChange={(e) => setFormData({ ...formData, type_notification: e.target.value })}
              required
            >
              {notificationTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.icon} {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Destinataires *
            </label>
            <select
              className="input-field"
              value={formData.recipient_type}
              onChange={(e) => setFormData({ ...formData, recipient_type: e.target.value })}
              required
            >
              {recipientTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.icon} {type.label}
                </option>
              ))}
            </select>
          </div>

          {formData.recipient_type === 'personnalise' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Emails (séparés par des virgules) *
              </label>
              <textarea
                className="input-field min-h-[100px]"
                placeholder="email1@example.com, email2@example.com, ..."
                value={formData.recipient_emails}
                onChange={(e) => setFormData({ ...formData, recipient_emails: e.target.value })}
                required={formData.recipient_type === 'personnalise'}
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sujet *
            </label>
            <input
              type="text"
              className="input-field"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              required
              placeholder="Ex: Nouvelle fonctionnalité disponible"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message *
            </label>
            <textarea
              className="input-field min-h-[150px]"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
              placeholder="Tapez votre message ici..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contenu HTML (optionnel)
            </label>
            <textarea
              className="input-field min-h-[200px] font-mono text-sm"
              value={formData.html_content}
              onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
              placeholder="<p>Votre contenu HTML personnalisé...</p>"
            />
            <p className="text-xs text-gray-500 mt-1">
              Si rempli, ce contenu remplacera le message dans l'email
            </p>
          </div>
        </form>
      </Modal>

      {/* Modal Prévisualisation */}
      <Modal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        title={`Prévisualisation - ${selectedNotification?.subject}`}
        size="xl"
      >
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <iframe
            srcDoc={previewHtml}
            className="w-full h-[600px] border-0"
            title="Preview"
          />
        </div>
      </Modal>

      {/* Modal Confirmation Envoi */}
      <Modal
        isOpen={isSendModalOpen}
        onClose={() => setIsSendModalOpen(false)}
        title="Confirmer l'envoi"
        footer={
          <div className="flex gap-4">
            <button 
              onClick={() => setIsSendModalOpen(false)} 
              className="btn-secondary"
              disabled={sending}
            >
              Annuler
            </button>
            <button 
              onClick={handleSend} 
              className="btn-primary"
              disabled={sending}
            >
              {sending ? 'Envoi en cours...' : '📤 Envoyer maintenant'}
            </button>
          </div>
        }
      >
        {selectedNotification && (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="font-semibold text-yellow-800 mb-2">⚠️ Attention</p>
              <p className="text-yellow-700 text-sm">
                Vous allez envoyer cette notification à <strong>{selectedNotification.recipient_type_display}</strong>.
                Cette action ne peut pas être annulée.
              </p>
            </div>

            <div>
              <p className="font-semibold text-gray-700 mb-2">Détails de la notification :</p>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p><strong>Sujet :</strong> {selectedNotification.subject}</p>
                <p><strong>Type :</strong> {selectedNotification.type_notification_display}</p>
                <p><strong>Destinataires :</strong> {selectedNotification.recipient_type_display}</p>
                {selectedNotification.recipient_type === 'personnalise' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Emails (séparés par des virgules)
                    </label>
                    <textarea
                      className="input-field min-h-[100px]"
                      placeholder="email1@example.com, email2@example.com, ..."
                      value={formData.recipient_emails}
                      onChange={(e) => setFormData({ ...formData, recipient_emails: e.target.value })}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
};

